import { User as FirebaseUser } from "firebase/auth"; // Import FirebaseUser type
import { UserDataInput } from "@/schemas/user"; // Use Zod-inferred type
import { Member, MemberPermission } from "@/types/schema"; // Corrected path for Member type

// getCurrentUserData function removed as it's obsolete.

/**
 * Synchronous version that returns default data
 * This is used as a fallback when async data is not available.
 * Now accepts firebaseUser to avoid direct auth calls.
 */
export const getDefaultUserData = (firebaseUser: FirebaseUser | null | undefined): UserDataInput => {
  const defaults: UserDataInput = {
    displayName: "",
    email: "", 
    status: "active", 
    avatarUrl: null,
    avatarInitials: "U",
    firstName: "",
    lastName: "",
    phone: "", 
    role: "Manager", 
    permissions: [],
    portfolios: [], 
    businesses: [], 
    // Timestamps should be null or actual Date objects if UserDataInput expects Date for defaults.
    // UserDataInput (from userSchema) will have these as Date | null | undefined after preprocess.
    // So, null is appropriate for a default.
    createdAt: null, 
    updatedAt: null,
    lastSeen: null,
    termsLastVerified: null,
    customClaims: null, 
  };

  if (firebaseUser) {
    return {
      ...defaults,
      avatarUrl: firebaseUser.photoURL || null,
      avatarInitials: (firebaseUser.displayName || firebaseUser.email || "U")
        .substring(0, 2)
        .toUpperCase(),
      firstName: firebaseUser.displayName?.split(" ")[0] || "",
      lastName: firebaseUser.displayName
        ? firebaseUser.displayName.split(" ").slice(1).join(" ")
        : "",
      email: firebaseUser.email || "", 
      phone: firebaseUser.phoneNumber || "", 
      displayName: firebaseUser.displayName || "",
    };
  }

  return defaults;
};

/**
 * Convert Member to compatible UserDataInput
 */
export const convertMemberToUserData = (member: Member): UserDataInput => {
  // Map fields from Member to UserDataInput.
  // Ensure all fields required by UserDataInput are present or handled.
  // Omit 'id' and 'userId' as they are not part of UserDataInput schema.
  const constructedDisplayName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
  return {
    displayName: constructedDisplayName || undefined, // userSchema.displayName is optional
    email: member.email, 
    status: member.status, 
    avatarUrl: member.avatarUrl || null,
    avatarInitials: member.avatarInitials || undefined,
    firstName: member.firstName || "",
    lastName: member.lastName || "",
    phone: member.phone || "",
    role: member.role, 
    permissions: member.permissions ? member.permissions as MemberPermission[] : [], 
    portfolios: member.portfolios || [], 
    businesses: member.businesses || [], 
    createdAt: undefined, 
    updatedAt: undefined,
    lastSeen: undefined,
    termsLastVerified: undefined,
    customClaims: undefined, // Member type does not have customClaims
  };
};
